import { TILE_RENDER_WIDTH, TILE_RENDER_HEIGHT } from './types'

const PUBLIC_URL = process.env.PUBLIC_URL || ''

export const SPRITESHEET_PATH = `${PUBLIC_URL}/assets/maps/world-map.json`
export const CLIFF_SPRITESHEET_PATH = `${PUBLIC_URL}/assets/maps/world-map-cliff.json`
/** Fog slices in world-map-cliff.png — same frames as route transition fog in FogTransitionContext. */
export const CLIFF_FOG_SPRITE_KEYS = ['fog-1.png', 'fog-2.png'] as const
export const MAP_OVERLAY_SPRITESHEET_PATH = `${PUBLIC_URL}/assets/maps/map-overlay.json`
/** River tile atlas (TexturePacker JSON + PNG); same layout as world_map_generator `river-overlay.json`. */
export const RIVER_OVERLAY_SPRITESHEET_PATH = `${PUBLIC_URL}/assets/maps/river-overlay.json`
/** Boundary decoration atlas (spikes, rock, tower, cylinder) for inter-group boundary lines. */
export const MAP_BOUNDARY_SPRITESHEET_PATH = `${PUBLIC_URL}/assets/maps/map-boundary.json`
/** User castle era art (frames `1.png`–`6.png`, TexturePacker + castles.png). */
export const CASTLES_SPRITESHEET_PATH = `${PUBLIC_URL}/assets/maps/castles.json`
/** Barbarian-village art per reward resource (frames `barbarian-{wheat,bread,wood,stone,iron,usd}.png`, TexturePacker + barbarian-villages.png). */
export const BARBARIAN_VILLAGES_SPRITESHEET_PATH = `${PUBLIC_URL}/assets/maps/barbarian-villages.json`
/** Auto-battle FX on the map (TexturePacker JSON + sword-animation.png under assets/maps/). */
export const SWORD_ANIMATION_JSON_PATH = `${PUBLIC_URL}/assets/maps/sword-animation.json`
/** Backward-compatible singular folder path if an environment uses `assets/map/`. */
export const SWORD_ANIMATION_JSON_FALLBACK_PATH = `${PUBLIC_URL}/assets/map/sword-animation.json`
/** Same atlas as hex-battle `public/assets/battle/` when maps copy is missing in deploy. */
export const SWORD_ANIMATION_JSON_BATTLE_FALLBACK_PATH = `${PUBLIC_URL}/assets/battle/sword-animation.json`

/** Starter castle protection badge (drawn above castles when `CastleData.newbieShieldActive` is true). */
export const CASTLE_SHIELD_WEBP_PATH = `${PUBLIC_URL}/assets/maps/castle-shield.webp`

/**
 * TexturePacker JSON paths plus their PNG siblings and standalone map images referenced by this engine.
 * Used for Vercel startup preload.
 */
export function getWorldMapTerrainEnginePreloadUrls(): string[] {
  const jsonPaths = [
    SPRITESHEET_PATH,
    CLIFF_SPRITESHEET_PATH,
    MAP_OVERLAY_SPRITESHEET_PATH,
    RIVER_OVERLAY_SPRITESHEET_PATH,
    MAP_BOUNDARY_SPRITESHEET_PATH,
    CASTLES_SPRITESHEET_PATH,
    BARBARIAN_VILLAGES_SPRITESHEET_PATH,
    SWORD_ANIMATION_JSON_PATH,
    SWORD_ANIMATION_JSON_FALLBACK_PATH,
    SWORD_ANIMATION_JSON_BATTLE_FALLBACK_PATH,
  ]
  const out = new Set<string>()
  for (const jp of jsonPaths) {
    if (!jp) continue
    out.add(jp)
    if (/\.json$/i.test(jp)) {
      out.add(jp.replace(/\.json$/i, '.png'))
    }
  }
  out.add(CASTLE_SHIELD_WEBP_PATH)
  return [...out]
}

// Source sprite dimensions from spritesheet (before scaling)
export const SPRITE_SOURCE_WIDTH = 214
export const SPRITE_SOURCE_HEIGHT = 170

// Overlay atlas source size (map-overlay.json forest sprites)
export const OVERLAY_SOURCE_WIDTH = 150
export const OVERLAY_SOURCE_HEIGHT = 150

export const FLAGPOLE_HEIGHT = 958

export const FLAGPOLE_OFFSET = {
  x: 25,
  y: 18,
}

export const FLAGPOLE_SCALE = {
  x: 0.5,
  y: 0.5,
}

export const FLAG_SCALE = {
  x: 0.5,
  y: 0.5,
}

export const FLAG_OFFSET = {
  x: 15.6,
  y: 12.7,
}

/** Default offset for castle owner name label (pixels from castle center). Override at runtime via engine.setCastleLabelOffset(x, y). */
export const LABEL_OFFSET = {
  x: 0,
  y: 65,
}

export const CASTLE_AREA_RADIUS = 2

// Flat-top hex vertices relative to center (clockwise from left point).
// RW = half tile width (circumradius to left/right points).
// RH = half tile height (inradius approximation to top/bottom flat edges).
const _RW = TILE_RENDER_WIDTH / 2
const _RH = TILE_RENDER_HEIGHT / 2
export const HEX_VERTICES = [
  { x: -_RW,      y:  0    }, // left
  { x: -_RW / 2,  y: -_RH  }, // top-left
  { x:  _RW / 2,  y: -_RH  }, // top-right
  { x:  _RW,      y:  0    }, // right
  { x:  _RW / 2,  y:  _RH  }, // bottom-right
  { x: -_RW / 2,  y:  _RH  }, // bottom-left
]

export const MAX_HIBERNATE = 50

// --- Procedural terrain variation (for future decals / region palettes) ---
/** When decals are implemented: probability per tile to place a small decoration (rock, bush, etc.). */
export const TERRAIN_DECAL_PROBABILITY = 0.1
/** Region size (hexes) for regional palette variation; hash(floor(q/SIZE), floor(r/SIZE)) picks palette. */
export const TERRAIN_REGION_SIZE = 32

export function getFlagColor(castleData: { flagColor?: number | null }): number {
  if (castleData.flagColor !== undefined && castleData.flagColor !== null) {
    return castleData.flagColor
  }
  return 0xffffff
}
